import { useState, useEffect, type CSSProperties } from "react";
import type { IDefaultInput } from "libs/design-system/src/types/Input/input";
import { Input } from "@unified-checkout/design-system";

import type { IColorPicker } from "@app/dashboard/types/innerLayout/color-picker";
import styles from "./color-picker.module.scss";


const MIN_HEX_LENGTH = 4;
const MAX_HEX_LENGTH = 7;
const DEFAULT_HEX_COLOR_REGEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

const convertToSixCharHex = (hexCode: String) => {
  return hexCode = hexCode.split("").map((char) => {
    if (char === "#") return char;
    return `${char}${char}`;
  }).join("");
};

const handleHexCodeFormat = (hexCode: CSSProperties['color']) => {
  return hexCode && hexCode.length === MIN_HEX_LENGTH ? convertToSixCharHex(hexCode) : hexCode;
};

const defaultLocalization = {
  en: {
    invalidColorFormat: "Invalid color format",
  },
  ar: {
    invalidColorFormat: "تنسيق لون غير صحيح",
  }
};


export const ColorPicker = ({
  lang = 'en',
  label,
  inputProps,
  defaultColor,
  colorValidationRegex = DEFAULT_HEX_COLOR_REGEX,
  onErrorHandler,
  onColorChangeHandler,
  localization
}: IColorPicker) => {

  const TEXT = { ...defaultLocalization[lang], ...(localization || {}) };

  const [color, setColor] = useState<CSSProperties['color']>(defaultColor);
  const [error, setError] = useState("");

  const onColorChange = (newColor: string) => {
    setColor(newColor);

    const errMsg = (colorValidationRegex && !colorValidationRegex.test(newColor)) ?
      TEXT.invalidColorFormat
      :
      "";

    onErrorHandler?.(errMsg);
    setError(errMsg);
  };

  useEffect(() => {
    onColorChangeHandler?.(color || "");
  }, [color]);

  return (
    <div className={styles["color-picker-wrapper"]}>
      {
        label &&
        <div className={styles["color-picker-wrapper__label"]}>
          {label}
        </div>
      }

      <div className={styles["color-picker-wrapper__input-box"]}>

        <Input
          {...inputProps as IDefaultInput}
          value={color}
          onChange={(e) => {
            onColorChange(e.target.value);
          }}
          status={!!(inputProps?.error || error) ? 'error' : 'default'}
        />

        <div
          className={styles["color-picker-wrapper__picker-trigger"]}
          style={{ backgroundColor: color }}
        >
          <input
            type="color"
            name={`${inputProps?.name}-input`}
            maxLength={MAX_HEX_LENGTH}
            value={handleHexCodeFormat(color)}
            onChange={(e) => setColor((e.target as HTMLInputElement).value)}
          />
        </div>
      </div>

      {
        (inputProps?.error || error) &&
        <div className={styles["color-picker-wrapper__err-msg"]}>
          {inputProps?.error || error}
        </div>
      }
    </div>
  );
}